import { action, computed, makeObservable, observable } from 'mobx';
import i18next from 'i18next';
import { ActionType, CancelTaskActionMetadata } from '@bringg/types';
import { getSharedRootStore, hasFeatureFlag } from '@bringg-frontend/global-stores';

import { ActionsRepo, ActionBaseStore } from './internal';
import { ActionFamilyType, ClientCalculateAvailability } from '../utils/types';
import { CALCULATE_AVAILABILITY_STATE_25_DAYS_FF } from '../../../consts';

const DEFAULT_VALID_GAP = 16;
const EXTENDED_VALID_GAP = 26;

export const getValidGap = (): number => {
	const currentUser = getSharedRootStore()?.data?.usersStore?.currentUser;

	if (currentUser && hasFeatureFlag(currentUser, CALCULATE_AVAILABILITY_STATE_25_DAYS_FF)) {
		return EXTENDED_VALID_GAP;
	}

	return DEFAULT_VALID_GAP;
};

class CalculateAvailabilityActionStore extends ActionBaseStore {
	type = ActionFamilyType.CALCULATE_AVAILABILITY_STATE;
	actionType = ActionType.CALCULATE_AVAILABILITY_STATE;
	fromDays: number;
	toDays: number;

	constructor(initAction: ClientCalculateAvailability, actionsRepo: ActionsRepo) {
		super(actionsRepo);

		makeObservable(this, {
			fromDays: observable,
			toDays: observable,
			setFromDays: action,
			setToDays: action,
			title: computed,
			isValid: computed,
			mappedToServer: computed,
			hasValueError: computed,
			hasGapError: computed
		});

		if (initAction.data?.from_days_ahead != null) {
			this.fromDays = initAction.data?.from_days_ahead;
		}
		if (initAction.data?.to_days_ahead != null) {
			this.toDays = initAction.data?.to_days_ahead;
		}
	}

	setFromDays = date => {
		this.fromDays = date;
	};

	setToDays = date => {
		this.toDays = date;
	};

	get mappedToServer() {
		return {
			action_type: this.actionType,
			data: {
				from_days_ahead: this.fromDays,
				to_days_ahead: this.toDays
			}
		};
	}

	get hasGapError(): boolean {
		return this.toDays - this.fromDays >= getValidGap();
	}

	get hasValueError(): boolean {
		return this.fromDays > this.toDays;
	}

	get isValid(): boolean {
		return this.fromDays > -1 && this.toDays > -1 && !this.hasValueError && !this.hasGapError;
	}

	get title(): string {
		const titleKey = (this.actionData as CancelTaskActionMetadata).translation_string;

		return i18next.t(titleKey);
	}
}

export default CalculateAvailabilityActionStore;
